import PropTypes from 'prop-types';
import { useMemo, useState } from 'react';

import {
  Card,
  DEFAULT_CHART_COLORS,
  isCalypsoSurvey,
  Layout,
  Lightbox,
  Pill,
  ProgressBar,
} from '@utilities';
import { SURVEY_TYPES } from '@api/instant_survey';

import GroupAdvancedPrompts from '../../../../../CreateInstantSurvey/components/Panelists/components/QuotaGroupCard/components/GroupAdvancedPrompts';
import GroupMadlib from '../../../../../CreateInstantSurvey/components/Panelists/components/QuotaGroupCard/components/GroupMadlib';
import {
  getIsPreviewDisabled,
  PreviewModal,
  useSurveyPreview,
} from '../../../../../../components/SurveyPreview';
import Questionnaire from '../Questionnaire';

import styles from './_index.module.scss';

const Details = ({ details, survey, surveyQuestions, surveyType }) => {
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const isMonadicSurvey = surveyType?.id === SURVEY_TYPES.MONADIC;
  const { conceptImages, groupImageIndex } = useMemo(() => {
    const images = [];
    const indexByGroupId = {};

    survey.groups.forEach((group) => {
      if (group?.conceptImage?.cdnUrl) {
        indexByGroupId[group.id] = images.length;
        images.push({
          alt: `Concept for ${group.name}`,
          caption: group?.conceptName || group.name,
          src: group.conceptImage.cdnUrl,
        });
      }
    });

    return { conceptImages: images, groupImageIndex: indexByGroupId };
  }, [survey.groups]);

  const surveyPreviewQuestions = survey.questions;
  const canPreviewSurvey = isCalypsoSurvey(survey);
  const {
    handleClosePreviewModal,
    handlePreviewButtonClick,
    handleUpdatePreviewQuestion,
    handleUpdatePreviewQuotaGroup,
    previewQuestions,
    previewSelection,
    resetPreviewQuestion,
    showPreviewModal,
  } = useSurveyPreview({
    questionLogic: survey.questionLogic,
    questions: surveyPreviewQuestions,
    quotaGroups: survey.groups,
  });
  const isPreviewButtonDisabled = getIsPreviewDisabled(previewQuestions);

  const handleOpenLightbox = (groupId) => {
    const imageIndex = groupImageIndex[groupId];
    if (imageIndex === undefined) return;
    setLightboxIndex(imageIndex);
    setIsLightboxOpen(true);
  };

  const handleCloseLightbox = () => {
    setIsLightboxOpen(false);
  };

  return (
    <div className={styles['details']}>
      <h3>Quota {survey.groups.length > 1 ? 'Groups' : 'Group'}</h3>
      <Layout.Flex.Column className={styles['details-list']}>
        {survey.groups.map((group, index) => (
          <Card className={styles['details-card']} key={group.id} status="info">
            {/* <EditableTitle value={group.name} /> */}
            <h2>
              <span
                className={styles['details-group-color']}
                style={{ background: DEFAULT_CHART_COLORS[index % DEFAULT_CHART_COLORS.length] }}
              />
              {group.name}
            </h2>
            {group?.continuouslyFed && <Pill isCompleted value="Continuously Fed Sample" />}
            <Layout.Flex gap="x-large">
              <Layout.Fill>
                <GroupMadlib group={group} isReadOnly surveyType={surveyType} />
                {surveyType.advancedPrompts?.length > 0 && (
                  <GroupAdvancedPrompts
                    advancedPrompts={surveyType.advancedPrompts}
                    group={group}
                    isCompleted
                  />
                )}
              </Layout.Fill>
              <div className={styles['details-group-divider']} />
              <Layout.Sidebar>
                <div
                  className={styles['details-group-response-counts']}
                  id={`response-counts-${group.id}`}
                >
                  Requested Quota <strong>{group.quotaLimit}</strong>
                </div>
                <ProgressBar
                  aria-labelledby={`response-counts-${group.id}`}
                  max={group.quotaLimit}
                  value={details?.completesByQuotaGroup?.[group.id] || group.responseCount}
                />
                {isMonadicSurvey && (group?.conceptImage?.cdnUrl || group?.conceptName) && (
                  <div className={styles['details-concept']}>
                    {group?.conceptImage?.cdnUrl && (
                      <button
                        aria-label={`Open concept for ${group.name}`}
                        className={styles['details-concept-button']}
                        onClick={() => handleOpenLightbox(group.id)}
                        type="button"
                      >
                        <img alt={`Concept for ${group.name}`} src={group?.conceptImage?.cdnUrl} />
                      </button>
                    )}
                    {group?.conceptName && <p>{group?.conceptName}</p>}
                  </div>
                )}
              </Layout.Sidebar>
            </Layout.Flex>
          </Card>
        ))}
      </Layout.Flex.Column>

      <Questionnaire
        canPreviewSurvey={canPreviewSurvey}
        isPreviewButtonDisabled={isPreviewButtonDisabled}
        onPreviewButtonClick={handlePreviewButtonClick}
        surveyQuestions={surveyQuestions}
      />

      {isMonadicSurvey && conceptImages.length > 0 && (
        <Lightbox
          currentImageIndex={lightboxIndex}
          images={conceptImages}
          isActive={isLightboxOpen}
          onClose={handleCloseLightbox}
        />
      )}
      {canPreviewSurvey && showPreviewModal && (
        <PreviewModal
          instantSurveyId={survey.id}
          isActive={showPreviewModal}
          onClose={handleClosePreviewModal}
          onQuestionChange={handleUpdatePreviewQuestion}
          onQuotaGroupChange={handleUpdatePreviewQuotaGroup}
          onRestart={resetPreviewQuestion}
          previewSelection={previewSelection}
          questions={previewQuestions}
          quotaGroups={survey.groups}
        />
      )}
    </div>
  );
};

Details.propTypes = {
  details: PropTypes.object,
  survey: PropTypes.object.isRequired,
  surveyQuestions: PropTypes.arrayOf(PropTypes.object),
  surveyType: PropTypes.object.isRequired,
};

export default Details;
